import React, { useState } from "react";
import { Modal, Form, Input, Button, notification, Flex } from "antd";
import { useMutation, useQueryClient } from "react-query";
import { createProduct, createProductVariant } from "../../../service";
import { useNavigate } from "react-router-dom";
import { colors } from "../../../styles/theme";
import { useTourContext } from "../../../contexts/tour-context/useTourContext";
import { useTranslation } from "react-i18next";
import { useBranchContext } from "../../../contexts/branch-context";
import { currencySymbol } from "../../../utils/currency";

interface AddProductModalProps {
  open: boolean;
  onCancel: () => void;
  categoryId: number;
  onProductCreated?: () => void;
}

export const AddProductModal: React.FC<AddProductModalProps> = ({
  open,
  onCancel,
  categoryId,
  onProductCreated,
}) => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const { productNameInputRef, productPriceInputRef, productDescInputRef, createProductBtnRef, isTourOpen, stepIndexes, advanceTourStep, tourStep } = useTourContext();

  const { mutateAsync: createProductMutation } = useMutation(createProduct, {
    onError: (error: any) => {
      notification.error({
        message: t("addProductModal.createErrorMessage"),
        description: error?.response?.data?.message || t("addProductModal.createErrorDescription"),
        style: {
          background: colors.errorBackground,
        },
        placement: "bottomRight",
      });
    },
  });

  const { mutateAsync: createVariantMutation } = useMutation(
    createProductVariant
  );

  const createProductWithVariant = async () => {
    const values = await form.validateFields();

    // Konwertuj cenę ze stringa na number (w złotych)
    const priceInZloty = values.price
      ? parseFloat(String(values.price).replace(/[^\d.]/g, ''))
      : null;

    // Konwertuj na grosze (backend przechowuje cenę w groszach jako Int)
    const priceInGrosze = priceInZloty !== null && !isNaN(priceInZloty)
      ? Math.round(priceInZloty * 100)
      : null;

    // Create product with basic data
    const newProduct = await createProductMutation({
      name: values.name,
      description: values.description || "",
      productCategoryId: categoryId,
      isVisible: true,
      temporaryHidden: false,
    });

    // Create default variant with the price
    if (newProduct?.id && priceInGrosze !== null) {
      await createVariantMutation({
        productBaseId: newProduct.id,
        name: t("addProductModal.defaultVariantName"),
        price: priceInGrosze,
        key: "default",
      });
    }

    // Invalidate queries to refresh the product list
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === `/products-category-${categoryId}`;
      },
    });

    return { newProduct, productName: values.name };
  };

  const handleSubmit = async () => {
    try {
      setIsCreating(true);

      const { newProduct } = await createProductWithVariant();

      notification.success({
        message: t("addProductModal.createSuccessMessage"),
        description: t("addProductModal.createSuccessDescription"),
        style: {
          background: colors.successBackground,
        },
        placement: "bottomRight",
      });

      // Przejdź dalej z otwartego okienka bezpośrednio do zobaczenia menu z globusa / hamburger_2
      if (isTourOpen) advanceTourStep(stepIndexes.PRODUCT_MODAL_OPEN);

      // Navigate to edit page (podczas touru pomijamy — zostajemy na /menu żeby pokazać globus)
      if (!isTourOpen) {
        navigate(`/edit-product/${newProduct.id}`);
      }

      // Reset form and close modal
      form.resetFields();
      onCancel();
    } catch (error) {
      console.error("Error creating product:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSubmitAndAddAnother = async () => {
    try {
      setIsCreating(true);
      const { productName } = await createProductWithVariant();

      notification.success({
        message: (
          <span>
            {t("addProductModal.createSuccessNamedPrefix")} <strong>{productName}</strong> {t("addProductModal.createSuccessNamedSuffix")}
          </span>
        ),
        style: {
          background: colors.successBackground,
        },
        placement: "bottomRight",
      });

      // Scroll kategorię do końca aby widać było nowy produkt
      if (onProductCreated) {
        onProductCreated();
      }

      // Reset form but keep modal open
      form.resetFields();
    } catch (error) {
      console.error("Error creating product:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t("addProductModal.title")}
      open={open}
      onCancel={handleCancel}
      closable={!isTourOpen}
      maskClosable={!isTourOpen}
      keyboard={!isTourOpen}
      width={500}
      footer={
        <Flex justify={isTourOpen ? "flex-end" : "space-between"}>
          {!isTourOpen && (
            <Button onClick={handleCancel}>
              {t("addProductModal.cancel")}
            </Button>
          )}
          <Flex gap={8}>
            {!isTourOpen && (
              <Button
                type="default"
                onClick={handleSubmitAndAddAnother}
                loading={isCreating}
              >
                {t("addProductModal.createAndAddAnother")}
              </Button>
            )}
            <Button
              ref={createProductBtnRef}
              type="primary"
              onClick={handleSubmit}
              loading={isCreating}
            >
              {t("addProductModal.createProduct")}
            </Button>
          </Flex>
        </Flex>
      }
    >
      <div style={{ marginBottom: "20px", color: colors.textSecondary, fontSize: "14px", lineHeight: "1.4" }}>
        {t("addProductModal.description")}
      </div>
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          name="name"
          label={t("addProductModal.nameLabel")}
          rules={[
            { required: true, message: t("addProductModal.nameRequired") },
          ]}
        >
          <Input
            ref={productNameInputRef}
            placeholder={t("addProductModal.namePlaceholder")}
            autoFocus
          />
        </Form.Item>

        <Form.Item
          name="price"
          label={t("addProductModal.priceLabel")}
          rules={[
            { required: true, message: t("addProductModal.priceRequired") },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject(t("addProductModal.priceRequired"));
                }
                const numValue = parseFloat(String(value).replace(/[^\d.]/g, ''));
                if (isNaN(numValue)) {
                  return Promise.reject(t("addProductModal.priceInvalid"));
                }
                if (numValue <= 0) {
                  return Promise.reject(t("addProductModal.priceGreaterThanZero"));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            ref={productPriceInputRef}
            placeholder="0.00"
            addonAfter={currencySymbol(currentBranch?.currency)}
            styles={{
              input: {
                borderTopRightRadius: '0px !important',
                borderBottomRightRadius: '0px !important',
                borderRight: '0 !important',
              },
            }}
            className="price-input-with-addon"
            onKeyDown={(e) => {
              // Zablokuj nieprawidłowe znaki - dozwolone tylko cyfry, kropka, przecinek, backspace, delete, tab, strzałki
              const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
              const isNumber = /^[0-9]$/.test(e.key);
              const isDotOrComma = e.key === '.' || e.key === ',';

              if (!isNumber && !isDotOrComma && !allowedKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              // Zamień przecinek na kropkę
              const value = e.target.value;
              if (value.includes(',')) {
                form.setFieldValue('price', value.replace(',', '.'));
              }
            }}
            onBlur={(e) => {
              // Po opuszczeniu pola - sformatuj wartość
              const value = e.target.value;
              if (value) {
                const numValue = parseFloat(value.replace(/[^\d.]/g, ''));
                if (!isNaN(numValue)) {
                  form.setFieldValue('price', numValue.toFixed(2));
                }
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={t("addProductModal.descriptionLabel")}
        >
          <Input.TextArea
            ref={productDescInputRef}
            placeholder={t("addProductModal.descriptionPlaceholder")}
            rows={3}
            showCount
            maxLength={500}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
